import { useContext, useState } from "react";
// import { LinkedAccountsContext } from "../../providers/LinkedAccountsProvider";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Download, Search } from "lucide-react";
import { BankingContext } from "../../providers/BankingProvider";
import { EmptyState } from "../../components/ui/empty-state";
import { Input } from "../../components/ui/input";
import CustomDatePicker from "../../components/common/CustomDatePicker";
import {
  categorizedColumns,
  exludedColumns,
  forReviewColumns,
} from "./components/BankingColumns";
import { DataTable } from "../../components/ui/data-table";
import { LinkedAccountsContext } from "../../providers/LinkedAccountsProvider";
import { TableSkeleton } from "../../components/Skeleton/TableSkeleton";
import { motion } from "framer-motion";
import { EditTransactionDialog } from "./components/EditTransactionDialog";
import { MatchTransactionDialog } from "./components/MatchTransactionDialog";

export const BankingData = () => {
  const {
    transactions,
    categories,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    handleSearch,
    handleSearchClick,
    handleDateChange,
    isEditTransactionModalOpen,
    handleChangeEditTransactionModal,
    confirmTransaction,
    rowSelection,
    setRowSelection,
    loading,
    excludeTransaction,
    bulkConfirmTransaction,
    undoCateogrizedTransaction,
    handlePageChange,
    handlePageSizeChange,
    pagination,
    handleDownloadTransactions,
    handleMatchTransaction,
    modalStates,
    categoriesCount,
  } = useContext(BankingContext);
  const { selectedAccountId } = useContext(LinkedAccountsContext);
  const [undoLoadingRowId, setUndoLoadingRowId] = useState<string | null>(null);
  const [isEditTransaction, setIsEditTransaction] = useState<string>("");
  let selectedRows =
    Array.isArray(transactions) && transactions.length > 0 && rowSelection
      ? Object.entries(rowSelection)
          .filter(([_, isSelected]) => isSelected)
          .map(([key]) => {
            const index = parseInt(key, 10);
            return transactions[index];
          })
          .filter(
            (transaction): transaction is (typeof transactions)[number] =>
              !!transaction
          )
      : [];

  const selectColumns = () => {
    if (selectedCategory == 46) {
      return [
        ...forReviewColumns(),
        {
          accessorKey: "action",
          header: "Action",
          cell: ({ row }: any) => {
            const selectedCount = Object.values(rowSelection || {}).filter(
              Boolean
            ).length;
            const isRowSelected = !!rowSelection?.[row.index];

            return (
              <>
                {row.original.isAssigned ? (
                  <Button
                    size="sm"
                    className="BookEZ_btn_green_bg BookEZ_btn_green_color"
                    loading={
                      loading?.isConfirming &&
                      undoLoadingRowId === row.original.transactionId
                    }
                    onClick={async () => {
                      await confirmTransaction(row.original.transactionId);
                    }}
                    disabled={
                      (loading?.isConfirming &&
                        undoLoadingRowId === row.original.transactionId) ||
                      // Disable if more than one row is selected
                      selectedCount > 1 ||
                      // If one row is selected and this row is not selected, disable
                      (selectedCount === 1 && !isRowSelected)
                    }
                  >
                    Confirm
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="BookEZ_btn_green_bg BookEZ_btn_green_color"
                    loading={
                      loading?.isConfirming &&
                      undoLoadingRowId === row.original.transactionId
                    }
                    onClick={() => {
                      setRowSelection &&
                        setRowSelection({ ...rowSelection, [row.index]: true });
                      handleChangeEditTransactionModal(true, [row.original]);
                    }}
                    disabled={
                      selectedCount > 1 ||
                      // If one row is selected and this row is not selected, disable
                      (selectedCount === 1 && !isRowSelected)
                    }
                  >
                    Apply
                  </Button>
                )}
              </>
            );
          },
        },
      ];
    } else if (selectedCategory == 47) {
      return [
        ...categorizedColumns(),
        {
          accessorKey: "action",
          header: "Action",
          cell: ({ row }: any) => (
            <Button
              size="sm"
              variant="underline"
              loading={undoLoadingRowId === row.original.transactionId}
              onClick={async () => {
                setUndoLoadingRowId(row.original.transactionId);
                try {
                  await undoCateogrizedTransaction?.(
                    row.original.transactionId
                  );
                } finally {
                  setUndoLoadingRowId(null);
                }
              }}
              disabled={undoLoadingRowId === row.original.transactionId}
            >
              Undo
            </Button>
          ),
        },
      ];
    } else {
      return [
        ...exludedColumns(),
        {
          accessorKey: "action",
          header: "Action",

          cell: ({ row }: any) => (
            <Button
              size="sm"
              variant="underline"
              loading={
                loading?.isundoCategorizedTransaction &&
                undoLoadingRowId === row.original.transactionId
              }
              onClick={async () => {
                setUndoLoadingRowId(row.original.transactionId);
                undoCateogrizedTransaction &&
                  (await undoCateogrizedTransaction(
                    row.original.transactionId
                  ));
                setUndoLoadingRowId(null);
              }}
              disabled={loading?.isundoCategorizedTransaction}
            >
              Undo
            </Button>
          ),
        },
      ];
    }
  };
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} // Start with 0 opacity
        animate={{ opacity: 1 }} // Animate to 1 opacity
        transition={{ duration: 1 }} // 1 second fade-in duration
      >
        {selectedAccountId && (
          <h2 className="BookEZ_font_bold BookEZ_font_color_gray_3 BookEZ_font_sub_heading py-2 BookEZ_font_header_color">
            Transactions
          </h2>
        )}
        {selectedAccountId && (
          <div className="mt-3 BookEZ_card_bg p-2 border BookEZ_card_border_color BookEZ_card_radius shadow-md">
            <div className="flex flex-wrap gap-4 justify-between my-2">
              <div className="flex py-1 justify-start items-center">
                {loading?.loadingCategories ? (
                  // Show loading skeletons or a spinner
                  <>
                    <div className="flex gap-2 bg-gray-100 p-2 rounded-md">
                      <span className="animate-pulse bg-gray-200 rounded-md h-8 w-24" />
                      <span className="animate-pulse bg-gray-200 rounded-md h-8 w-24" />
                      <span className="animate-pulse bg-gray-200 rounded-md h-8 w-24" />
                    </div>
                  </>
                ) : categories && categories.length > 0 ? (
                  <Tabs
                    value={selectedCategory?.toString()}
                    onValueChange={(value: string) => {
                      setSelectedCategory(Number(value));
                    }}
                  >
                    <TabsList className="flex sm:flex-row! flex-col! gap-4 pt-1 justify-start items-center BookEZ_font_color_gray_2">
                      {categories.map((cat) => {
                        // Find the matching recordCount for the current category's masterId
                        const matchingCount = categoriesCount.find(
                          (count) => count.recordType === cat.masterId
                        );

                        return (
                          <TabsTrigger
                            key={cat.masterId}
                            className="flex items-center justify-between gap-2 px-3 py-1 rounded-md"
                            value={cat.masterId.toString()}
                          >
                            {cat.codeName}{" "}
                            <span className="text-black bg-white px-2 py-1 rounded-md text-xs font-semibold">
                              {matchingCount ? matchingCount.recordCount : 0}{" "}
                              {/* Show count */}
                            </span>
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </Tabs>
                ) : (
                  <div className="text-muted-foreground px-2">
                    No categories found
                  </div>
                )}
              </div>
              {selectedRows.length == 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {/* Download button with margin on top for mobile */}
                  <div className="relative">
                    <Input
                      type="text"
                      id="search Transaction"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="pr-10"
                      disabled={!selectedCategory || !selectedAccountId}
                    />
                    <Search
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 BookEZ_font_color_gray_1 disabled:cursor-not-allowed"
                      aria-hidden="true"
                      onClick={() => {
                        handleSearchClick();
                      }}
                    />
                  </div>
                  <CustomDatePicker
                    onDateRangeChange={(startDate, endDate) => {
                      handleDateChange && handleDateChange(startDate, endDate);
                    }}
                    defaultDateRangeOption="this-month"
                    disabled={!selectedCategory || !selectedAccountId}
                  />

                  <Button
                    variant={"outline"}
                    className="BookEZ_card_bg mt-2 sm:mt-0"
                    disabled={
                      !selectedCategory ||
                      !selectedAccountId ||
                      loading?.isDownloading ||
                      transactions?.length == 0
                    }
                    onClick={handleDownloadTransactions}
                    loading={loading?.isDownloading}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            {selectedRows.length >= 1 && selectedCategory == 46 && (
              <div className="flex items-center gap-3 flex-wrap bg-gray-800 text-white BookEZ_card_radius px-6 py-4 mb-3">
                <span>{selectedRows.length} Transactions selected</span>
                {!selectedRows[0].isAssigned && selectedRows?.length === 1 && (
                  <Button
                    variant="outline"
                    className=""
                    loading={loading?.isApplyingTransaction}
                    onClick={() => {
                      handleChangeEditTransactionModal(true, selectedRows);
                    }}
                  >
                    Apply
                  </Button>
                )}
                {selectedRows.every((row) => row.isAssigned) && (
                  <Button
                    variant="outline"
                    className=""
                    disabled={loading?.isbulkConfirmingTransaction}
                    loading={loading?.isbulkConfirmingTransaction}
                    onClick={() => {
                      bulkConfirmTransaction &&
                        bulkConfirmTransaction(selectedRows);
                    }}
                  >
                    Confirm
                  </Button>
                )}
                {selectedRows?.length === 1 &&
                  selectedRows[0]?.isAssigned &&
                  selectedRows[0]?.recordTypeName === "For Review" && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditTransaction(
                          selectedRows[0]?.transactionId || ""
                        );
                        handleChangeEditTransactionModal(true, selectedRows);
                      }}
                    >
                      Edit Category
                    </Button>
                  )}
                {selectedRows?.length === 1 && (
                  <Button
                    variant="outline"
                    loading={loading?.isMatching}
                    onClick={() => {
                      handleMatchTransaction(true, selectedRows);
                    }}
                  >
                    Match
                  </Button>
                )}
                <Button
                  variant="outline"
                  loading={loading?.isExludingTransaction}
                  disabled={loading?.isExludingTransaction}
                  onClick={() => {
                    excludeTransaction && excludeTransaction(selectedRows);
                  }}
                >
                  Exclude
                </Button>
                <button
                  className="ml-auto text-white"
                  onClick={() => setRowSelection && setRowSelection({})}
                  title="Clear selection"
                >
                  ×
                </button>
              </div>
            )}

            {loading?.trnasactionsLoading ? (
              <TableSkeleton />
            ) : transactions?.length == 0 ? (
              <EmptyState title="No Transactions Found" />
            ) : (
              <DataTable
                columns={selectColumns()}
                data={transactions || []}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection as any}
                showPagination={true}
                pageSize={pagination?.pageSize}
                currentPage={pagination?.pageNumber}
                totalPages={pagination?.totalPages}
                totalRecords={pagination?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}

            <EditTransactionDialog
              open={isEditTransactionModalOpen ?? false}
              onOpenChange={handleChangeEditTransactionModal}
              selectedRows={selectedRows}
              isEditTransaction={isEditTransaction}
              setIsEditTransaction={setIsEditTransaction}
            />
            <MatchTransactionDialog
              open={modalStates?.isMatchTransactionModalOpen ?? false}
              onOpenChange={(open) =>
                handleMatchTransaction(open, selectedRows)
              }
            />
          </div>
        )}
      </motion.div>
    </>
  );
};
